import { useScrollNavigation } from '@app/src/context/ScrollNavigationContext'
import { Close } from '@mui/icons-material'
import { Box, Button, Dialog, DialogContent } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ResponsiveScrollNavigation from '../SustainabilityReport/ResponsiveScrollNavigation'
import TopicalRiskMethodologyContent from './TopicalRiskMethodologyContent'

interface TopicalRiskMethodologyModalProps {
  open: boolean
  onClose: () => void
  /** Section to jump to once the modal has finished opening. Defaults to the top of the content. */
  initialSection?: string
}

function TopicalRiskMethodologyModal({
  open,
  onClose,
  initialSection,
}: TopicalRiskMethodologyModalProps): React.ReactElement {
  const { formatMessage } = useIntl()
  const { registerNavigationItems, scrollToSection } = useScrollNavigation()

  const sections = useMemo(
    () => [
      { id: 'riskMethodology', label: formatMessage({ id: 'topicalRisk.methodology.riskMethodology' }) },
      { id: 'howItWorks', label: formatMessage({ id: 'topicalRisk.methodology.howItWorks' }) },
      { id: 'topicRisk', label: formatMessage({ id: 'topicalRisk.methodology.topicRisk' }) },
      { id: 'flags', label: formatMessage({ id: 'topicalRisk.methodology.flags' }) },
      { id: 'overallRisk', label: formatMessage({ id: 'topicalRisk.methodology.overallRisk' }) },
      {
        id: 'adaptToYourOrganization',
        label: formatMessage({ id: 'topicalRisk.methodology.adaptToYourOrganization' }),
      },
    ],
    [formatMessage],
  )

  useEffect(() => {
    registerNavigationItems(sections)
  }, [registerNavigationItems, sections])

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      scroll="paper"
      // Section refs only exist once the dialog content has mounted, so deep-link on entered.
      TransitionProps={{ onEntered: () => initialSection && scrollToSection(initialSection) }}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: 1,
        },
      }}
    >
      <DialogContent
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '10px',
            right: '25px',
            zIndex: 1001,
          }}
        >
          <Box sx={{ bgcolor: { xs: 'transparent', md: 'white' }, borderRadius: 8, p: 1 }}>
            <Button onClick={onClose} color="primary" endIcon={<Close />}>
              {formatMessage({ id: 'general.close' })}
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', minHeight: '100%' }}>
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 0, md: 0, lg: '40px' },
              left: '32px',
              alignSelf: 'flex-start',
              zIndex: { xs: 1000, md: 'auto' },
              pr: { xs: '130px', md: 0 },
              bgcolor: { xs: 'common.white' },
            }}
          >
            <ResponsiveScrollNavigation breakpoint="lg" items={sections} />
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              px: { xs: 2, md: 4 },
              pt: 7,
              pb: 12.5,
            }}
          >
            <Box sx={{ maxWidth: '680px', width: '100%' }}>
              <TopicalRiskMethodologyContent />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default TopicalRiskMethodologyModal
